import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import Topbar from "@/components/Topbar";
import { TimeProvider } from "@/context/TimeContext";
import { api } from "@/lib/api";
import { AlertTriangle, Info, X, CloudSnow, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SEV = {
  danger: { cls: "bg-primary text-primary-foreground", icon: CloudSnow },
  warning: { cls: "bg-amber-500 text-black", icon: AlertTriangle },
  info: { cls: "bg-blue-600 text-white", icon: Info },
};

function AlertBanner() {
  const [alerts, setAlerts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dismissed, setDismissed] = useState(() => JSON.parse(sessionStorage.getItem("mh-dismissed") || "[]"));

  useEffect(() => {
    api.get("/alerts").then(({ data }) => setAlerts(data.filter((a) => a.active))).catch(() => {});
  }, []);

  const visible = alerts.filter((a) => !dismissed.includes(a.id));
  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    sessionStorage.setItem("mh-dismissed", JSON.stringify(next));
  };

  if (visible.length === 0) return null;

  return (
    <div data-testid="alert-banner">
      {visible.map((a) => {
        const cfg = SEV[a.severity] || SEV.info;
        const Icon = cfg.icon;
        return (
          <div key={a.id} className={`flex items-center gap-3 px-4 lg:px-6 py-2.5 text-sm ${cfg.cls}`}>
            <Icon className="h-4 w-4 shrink-0" />
            <button data-testid="alert-banner-item" onClick={() => setSelected(a)}
              className="flex items-center gap-2 min-w-0 flex-1 text-left hover:underline underline-offset-2">
              <span className="font-semibold shrink-0">{a.title}</span>
              <span className="hidden sm:inline opacity-90 truncate">— {a.message}</span>
              <ChevronRight className="h-4 w-4 opacity-80 shrink-0" />
            </button>
            <button data-testid="alert-dismiss" onClick={() => dismiss(a.id)} aria-label="Dismiss" className="opacity-80 hover:opacity-100 shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="alert-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selected && (() => { const C = (SEV[selected.severity] || SEV.info).icon; return <C className="h-5 w-5 text-primary" />; })()}
              {selected?.title}
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed whitespace-pre-line text-foreground/80">
              {selected?.message}
            </DialogDescription>
          </DialogHeader>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Severity: {selected?.severity}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Layout() {
  return (
    <TimeProvider>
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar />
        <AlertBanner />
        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
    </TimeProvider>
  );
}
